import type { ReactNode } from 'react'
import type { Messages } from './ko'

export const ja: Messages = {
  app: {
    layout: {
      settings: '設定',
    },
    /**
     * 開き直す를 쓰지 않는다. 開き直る가 뻔뻔하게 나온다는 뜻으로 널리 쓰여 그 결이 따라온다.
     * 이 자리의 일본어 화면이 쓰는 말은 読み込み直す이고 記録を読み込む와 같은 동사로 선다.
     */
    update: {
      message: 'mlogが少し変わりました。読み込み直すと新しい画面になります。',
      reopening: '新しい画面を読み込んでいます',
      reopen: '読み込み直す',
    },
    notFound: {
      message: 'ページが見つかりません',
      back: '記録に戻る',
    },
    common: {
      retry: 'もう一度試す',
    },
  },

  auth: {
    gate: {
      /**
       * 공간을 場所로 옮긴다. スペース는 화면 여백을 가리키는 말로 먼저 읽히고, 소개 문구의
       * 自分だけの場所와 낱말이 어긋난다.
       */
      opening: '少しお待ちください。記録の場所を開いています',
      unreachable: 'いまは接続できません。少し経ってからもう一度試してください。',
    },
    login: {
      tagline: '気持ちを書きとめておく自分だけの場所',
      /**
       * 소개 문구가 이미 自分だけ를 썼으므로 안심 문장에서는 상대를 가리키지 않고 사실만
       * 말한다.
       */
      assurance: 'ここに残した記録はほかの誰にも見えません。',
      establishing: '入る準備をしています',
      autoSignUp: 'アカウントがなければ、このボタンでそのまま作られます。',
      consent: (terms: ReactNode, privacy: ReactNode): ReactNode => (
        <>
          続けると、{terms}と{privacy}に同意したものとみなします。
        </>
      ),
      termsLink: '利用規約',
      privacyLink: 'プライバシーポリシー',
      sessionExpired: '時間が経ってログインが切れました。入り直せば記録はそのままです。',
    },
    failure: {
      declined: '大丈夫です。準備ができたら、もう一度押してください。',
      emailAlreadyRegistered:
        'このメールアドレスで作った場所がすでにあります。最初に使った方法で入ってから、設定でこの方法を追加できます。',
      /**
       * 사과를 겹치지 않는다. 막힌 자리마다 申し訳ありません을 붙이면 이 앱이 계속 미안해하는
       * 자리가 된다.
       */
      emailUnavailable: (provider: string) =>
        `${provider}アカウントのメールアドレスを確認できなかったため、まだ場所を作れていません。${provider}でメールアドレスの確認を終えてから、もう一度押してください。`,
      unavailable: 'ログインできませんでした。もう一度押してください。',
    },
    provider: {
      google: 'Google',
      /**
       * 이 회사가 일본에서 스스로 쓰는 표기가 로마자 NAVER다. 뉴스 기사의 ネイバー는 회사를
       * 부르는 말이지 로그인 버튼에 서는 브랜드 표기가 아니다.
       */
      naver: 'NAVER',
      continueWith: (provider: string) => `${provider}で続ける`,
      leavingTo: (provider: string) => `${provider}に移動しています`,
    },
    logout: {
      label: 'ログアウト',
      pending: 'ログアウトしています',
      failed: 'ログアウトできませんでした。もう一度押してください。',
    },
  },

  onboarding: {
    /** 소개 문구가 쓰는 自分だけ를 그대로 받는다. 상대를 가리키는 말을 세우지 않는다. */
    greeting: 'ここは自分だけの場所です。',
    from: (provider: string) => `いまは${provider}で入りました。`,
    suggest: (provider: string) =>
      `${provider}も追加しておくと、どちらを押しても同じ場所に入れます。`,
    add: (provider: string) => `${provider}を追加`,
    skip: 'このまま入る',
    later: '設定からいつでも追加できます。',
  },

  link: {
    addedNotice: (provider: string) => `${provider}をログイン方法に追加しました。`,
    close: '閉じる',
  },

  entry: {
    list: {
      compose: '書いてみる',
      loading: '記録を読み込んでいます',
      failed: '記録を読み込めませんでした。少し経ってからもう一度試してください。',
      empty: '今日はどんな一日でしたか',
      emptyWelcome: '場所が開きました。今日はどんな一日でしたか',
      loadMore: '前の記録をもっと見る',
      loadingMore: '読み込んでいます',
    },
    editor: {
      opening: '記録を開いています',
      openFailed: '記録を開けませんでした',
      moodHint: 'いまの気持ちに近いものを選べます。選ばなくても大丈夫です。',
      placeholder: 'いま浮かんだままに書いてみてください',
      bodyLabel: '記録の本文',
      saving: '保存しています',
      saved: '保存しました',
      saveFailed: '保存できませんでした',
    },
    mood: {
      calm: 'おだやか',
      glad: 'うれしい',
      tired: 'つかれた',
      sad: 'しずんだ',
      /**
       * 여기만 한자로 둔다. 히라가나로 적으면 ふあん이 낱말로 서지 않고, もやもや는 뜻이
       * 흐릿한 답답함 쪽으로 기운다.
       */
      anxious: '不安',
    },
  },

  settings: {
    screen: {
      title: '設定',
      back: '戻る',
    },
    language: {
      label: '言語',
      sheetTitle: '言語',
      sheetHint: '変わるのは画面の言葉だけです。書いた記録とニックネームはそのままです。',
      system: 'システムのデフォルト',
      saveFailed: '言語の設定をアカウントに保存できませんでした。もう一度押してください。',
      close: '閉じる',
    },

    nickname: {
      /*
       * 名前는 실명을 적는 칸이고 あだ名는 놀림의 결이 붙으며 ハンドルネーム는 공개된 자리의
       * 표기를 가리켜 셋 다 뺐다. ニックネーム는 가입 화면에서 오래 쓰여 온 말이다.
       */
      label: 'ニックネーム',
      sheetTitle: 'ニックネーム',
      /* 가입 화면에서 익은 말이라 남이 본다는 오해가 옅다. 앞 문장이 그만큼만 받는다. */
      sheetHint: 'ニックネームは自分にしか見えません。好きなように付けて大丈夫です。',
      regenerate: '作り直す',
      regenerating: 'ニックネームを考えています',
      regenerateFailed: 'ニックネームを作れませんでした。もう一度押してください。',
      save: '保存',
      saving: '保存しています',
      saveFailed: 'ニックネームを保存できませんでした。もう一度押してください。',
      loadFailed: 'ニックネームを読み込めませんでした。',
      /* 수에 따라 갈리지 않아 한 형태만 갖는다. */
      remaining: (count: number) => `あと${count}文字書けます`,
      over: (count: number) => `あと${count}文字減らせば大丈夫です`,
      close: '閉じる',
    },
    /**
     * 連携를 쓰지 않는다. 밖으로 무엇이 건너가는지 묻게 만들어 나만 본다는 약속과 부딪치고
     * 連携解除라는 짝이 따라온다. ログイン은 記録과 겹치는 문제가 없어 그대로 쓴다.
     */
    loginMethod: {
      label: 'ログイン方法',
      description: '追加した方法なら、どれを押しても同じ場所に入れます。',
      added: '追加済み',
      add: '追加',
      addName: (provider: string) => `${provider}を追加`,
      linkedName: (provider: string) => `${provider}でログインできます`,
      addedNotice: (provider: string) => `${provider}を追加しました。`,
      alreadyLinked: (provider: string) =>
        `この${provider}アカウントは、ほかの場所ですでに使われています。別のアカウントで追加できます。`,
      /** 막힌 일에 사과를 앞세우지 않는다. 원인을 제품이 지고 담담하게만 적는다. */
      failed: '追加できませんでした。もう一度押してください。',
      loadFailed: 'ログイン方法を読み込めませんでした。',
    },
    theme: {
      label: '雰囲気',
      light: '晴れた昼',
      dark: '静かな夜',
      dusk: '夕焼け',
    },
    legal: {
      terms: '利用規約',
      privacy: 'プライバシーポリシー',
    },
  },

  legal: {
    document: {
      back: '戻る',
      effectiveDate: (date: string) => `施行日 ${date}`,
    },
  },
}
